import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type {
  AgendaChangeEntity,
  AgendaChangeEventDto,
  AgendaChangesResponse,
  AgendaChangeVerb,
} from '@familyhub/shared';
import { api, isOffline } from '../api/client';
import { useSession } from '../session';
import { useRefreshOnReturn } from '../useRefreshOnReturn';
import { parentColor, orderByParticipantName } from './labels';
import { formatEventDay, formatTimeRange } from './datetime';
import { groupAgendaChanges, type AgendaChangeCard } from './changesGrouping';
import { Loading } from '../components/Loading';

/**
 * The change log (docs/AGENDA.md §6): who added, changed or removed what.
 *
 * Grouped into a card per person per sitting — the same shape as the groceries
 * activity feed it is modelled on (`web/src/activity/grouping.ts`, rendered by
 * `ActivityScreen`) — rather than one row per edit. A Tuesday-evening pass that
 * touches five things used to be five near-identical lines to scroll past, each
 * reading "Jurren — activiteit gewijzigd" with no way to tell them apart without
 * opening each one; now it is one card with a plain-Dutch summary and the five
 * things listed underneath it.
 *
 * Every item carries **when the changed thing is**, not just its name: "Zwemles"
 * on its own tells the reader nothing they can act on, and the question this
 * screen is actually asked is "what did the other one do to my week?" — which
 * is answered by the day, the time range, whose it is, and whether it takes the
 * car. It describes the row **as it stands now** rather than what the edit
 * altered: this is a projection with no previous version to diff against, so a
 * claimed before/after would be invented.
 *
 * There is no accept/decline handshake any more — anyone may edit anything — so
 * this, not a permission wall, is how an edit becomes visible to the person who
 * didn't make it. Server-derived and fetched on mount, like the groceries feed:
 * read on the sofa, not in a shop, so it does not join the offline core.
 */

function initialOf(name: string): string {
  for (const char of name) {
    if (/\p{L}|\p{N}/u.test(char)) return char.toLocaleUpperCase('nl-NL');
  }
  return '?';
}

/** "Vandaag" / "Gisteren" / "Woensdag — 12 augustus", in the household's day. */
function dateHeader(iso: string): string {
  const at = new Date(iso);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (at.toDateString() === today.toDateString()) return 'Vandaag';
  if (at.toDateString() === yesterday.toDateString()) return 'Gisteren';
  const weekday = new Intl.DateTimeFormat('nl-NL', { weekday: 'long' }).format(at);
  const date = new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'long' }).format(at);
  return `${weekday.charAt(0).toUpperCase()}${weekday.slice(1)} — ${date}`;
}

/**
 * Shown per item, but only when a card mixes entity kinds — see `cardSummary`.
 * A card of one kind says so in its summary line already, and a tag on every
 * row of it would be the same word repeated down the card.
 */
const ENTITY_TAG: Record<AgendaChangeEntity, string> = {
  agenda_activities: 'Activiteit',
  agenda_childcare: 'Opvang',
  agenda_people: 'Persoon',
};

const ENTITY_NOUN: Record<AgendaChangeEntity, { one: string; many: string }> = {
  agenda_activities: { one: 'activiteit', many: 'activiteiten' },
  agenda_childcare: { one: 'opvangmoment', many: 'opvangmomenten' },
  agenda_people: { one: 'persoon', many: 'personen' },
};

const VERB_PAST: Record<AgendaChangeVerb, string> = {
  created: 'toegevoegd',
  updated: 'gewijzigd',
  deleted: 'verwijderd',
};

/** Childcare being arranged reads as "geregeld", not "toegevoegd". */
function verbPast(entity: AgendaChangeEntity | null, verb: AgendaChangeVerb): string {
  if (entity === 'agenda_childcare' && verb === 'created') return 'geregeld';
  return VERB_PAST[verb];
}

/**
 * "Jurren heeft 2 activiteiten gewijzigd." Falls back to a generic noun when a
 * sitting mixes activities, childcare and people — that stays honest rather
 * than picking one entity's noun for a card that isn't only that entity.
 */
function cardSummary(card: AgendaChangeCard): string {
  const n = card.events.length;
  const entities = new Set(card.events.map((e) => e.entity));
  const only = entities.size === 1 ? [...entities][0]! : null;
  const noun = only
    ? n === 1
      ? ENTITY_NOUN[only].one
      : ENTITY_NOUN[only].many
    : n === 1
      ? 'wijziging'
      : 'wijzigingen';
  return `${card.actor.display_name} heeft ${n} ${noun} ${verbPast(only, card.verb)}`;
}

/**
 * The line under an item's name: when it is, whose it is, and the two flags
 * worth a word — "wo 20 aug · 15:00–16:00 · Jurren · auto nodig". Assembled
 * here rather than server-side because it is display text, and every part of it
 * is skipped when the row has nothing to say (a person has no window at all).
 */
function detailOf(event: AgendaChangeEventDto): string {
  const parts: string[] = [];
  if (event.starts_at) {
    parts.push(formatEventDay(event.starts_at));
    parts.push(formatTimeRange(event.starts_at, event.ends_at, event.all_day));
  }
  if (event.participants.length > 0)
    parts.push(orderByParticipantName(event.participants, (n) => n).join(' en '));
  if (event.needs_car) parts.push('auto nodig');
  if (event.recurring) parts.push('herhaalt');
  return parts.join(' · ');
}

export function ChangesScreen() {
  const session = useSession();
  const userId = session.status === 'in' ? session.userId : '';
  const me = session.status === 'in' ? session.me : null;
  const memberIds = useMemo(() => {
    if (me?.members?.length) return me.members.map((m) => m.id);
    return userId ? [userId] : [];
  }, [me, userId]);

  const [events, setEvents] = useState<AgendaChangeEventDto[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  async function load() {
    try {
      setEvents((await api<AgendaChangesResponse>('/api/v1/agenda/changes')).events);
      setProblem(null);
    } catch (err) {
      setProblem(isOffline(err) ? 'Voor wijzigingen is verbinding nodig.' : 'Kon de wijzigingen niet laden.');
    }
  }

  useEffect(() => {
    void load();
  }, []);

  // A PWA left open for hours shows a stale snapshot otherwise — refresh on the
  // same triggers the sync loop uses.
  useRefreshOnReturn(load);

  const cards = events ? groupAgendaChanges(events) : [];

  return (
    <div className="app">
      <div className="topbar">
        <Link className="btn btn-quiet back" to="/agenda" aria-label="Terug naar de agenda">‹ Agenda</Link>
        <h1>Wijzigingen</h1>
      </div>

      <main className="stack">
        {problem && <p className="banner">{problem}</p>}

        {events && events.length === 0 && !problem && <p className="empty">Nog geen wijzigingen.</p>}

        {cards.map((card) => {
          const mixed = new Set(card.events.map((e) => e.entity)).size > 1;
          const color = card.actor.id ? parentColor(card.actor.id, memberIds) : 'var(--text-dim)';
          return (
            <article className="card activity-card" key={card.key}>
              <div className="activity-head">
                <span
                  className="activity-avatar agenda-change-avatar"
                  aria-hidden="true"
                  style={{ '--change-color': color } as React.CSSProperties}
                >
                  {initialOf(card.actor.display_name)}
                </span>
                <span className="activity-head-text">
                  <span className="activity-date">{dateHeader(card.at)}</span>
                  <span className="activity-summary">{cardSummary(card)}</span>
                </span>
              </div>

              <ul className={`activity-items activity-items-${card.verb}`}>
                {card.events.map((event) => {
                  const detail = detailOf(event);
                  return (
                    <li
                      className="activity-item agenda-change-item"
                      key={`${event.entity}:${event.id}:${event.verb}:${event.at}`}
                    >
                      <span className="agenda-change-title">
                        {mixed && <span className="badge">{ENTITY_TAG[event.entity]}</span>}
                        <span className="activity-item-name">{event.summary}</span>
                      </span>
                      {detail && <span className="agenda-change-detail">{detail}</span>}
                    </li>
                  );
                })}
              </ul>
            </article>
          );
        })}

        {!events && !problem && <Loading />}
      </main>
    </div>
  );
}
